import type { Readable, Writable } from 'svelte/store';
import { writable, get } from 'svelte/store';
import * as z from 'zod';
import * as Result from '$lib/utils/result';
import { type JsonValue, safeParse, safeParseJson } from '$lib/utils/zod';

export function createSessionStorable<T, Encoded extends JsonValue>(options: {
    key: Readable<string>;
    isBrowser: boolean;
    defaultData: T;
    zodSchema: z.ZodSchema<Encoded>;
    encode: (data: T) => Encoded;
    decode: (v: Encoded) => T;
}): Writable<T> {
    const { key, isBrowser, defaultData, zodSchema, encode, decode } = options;
    const store = writable(defaultData);

    key.subscribe(($key) => {
        if (isBrowser) {
            const item = sessionStorage.getItem($key) ?? null;
            if (item != null) {
                const data = Result.mapOk(
                    Result.mapOk(safeParseJson(item), (json) =>
                        safeParse(zodSchema, json),
                    ),
                    (data) => Result.ok(decode(data)),
                );
                if (data.status === 'ok') {
                    store.set(data.data);
                    return;
                }
            }
        }
        store.set(defaultData);
    });

    return {
        subscribe: store.subscribe,
        set: (data) => {
            if (isBrowser) {
                sessionStorage.setItem(get(key), JSON.stringify(encode(data)));
            }
            store.set(data);
        },
        update: (cb) => {
            const data = cb(get(store));
            if (isBrowser) {
                sessionStorage.setItem(get(key), JSON.stringify(encode(data)));
            }
            store.set(data);
        },
    };
}

export function createSessionBoolStorable({
    key,
    isBrowser,
    defaultData,
}: {
    key: Readable<string>;
    isBrowser: boolean;
    defaultData: boolean;
}) {
    return createSessionStorable<boolean, boolean>({
        key,
        isBrowser,
        defaultData,
        zodSchema: z.boolean(),
        encode: (a) => a,
        decode: (a) => a,
    });
}
